import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faReceipt, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { Button, Card, Col, Form, Row } from "@themesberg/react-bootstrap";

import { getVendorProductById, placeVendorOrder } from "../api/vendor.api";
import { useToast } from "../components/ToastProvider";
import { clearCart, getCartItems, removeCartItem, setCartItems as persistCartItems, updateCartItemQuantity } from "../utils/cartStorage";
import { resolveAssetUrl } from "../utils/assetUrl";

const sanitizeQuantityInput = (value) => value.replace(/[^\d]/g, "");

export default () => {
  const { showToast } = useToast();
  const [cartItems, setCartItemsState] = useState(getCartItems());
  const [draftQuantities, setDraftQuantities] = useState(() => (
    getCartItems().reduce((accumulator, item) => {
      accumulator[item.variant_id] = String(item.quantity || 1);
      return accumulator;
    }, {})
  ));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const enrichCartImages = async () => {
      const itemsMissingVariantImage = cartItems.filter((item) => !item.variant_image_url && item.product_id && item.variant_id);
      if (!itemsMissingVariantImage.length) {
        return;
      }

      try {
        const uniqueProductIds = [...new Set(itemsMissingVariantImage.map((item) => item.product_id))];
        const products = await Promise.all(uniqueProductIds.map((productId) => getVendorProductById(productId)));
        const productsById = products.reduce((accumulator, product) => {
          accumulator[product.id] = product;
          return accumulator;
        }, {});

        const nextItems = cartItems.map((item) => {
          if (item.variant_image_url || !item.product_id || !item.variant_id) {
            return item;
          }

          const product = productsById[item.product_id];
          const variant = product?.variants?.find((entry) => Number(entry.id) === Number(item.variant_id));
          const variantImageUrl = Array.isArray(variant?.images) && variant.images.length > 0
            ? variant.images[0].image_url
            : (variant?.image_url || "");

          return {
            ...item,
            variant_image_url: variantImageUrl
          };
        });

        persistCartItems(nextItems);
        setCartItemsState(nextItems);
      } catch (error) {
        // Leave the cart visible even if image enrichment fails.
      }
    };

    enrichCartImages();
  }, [cartItems]);
  const totalItems = useMemo(
    () => cartItems.reduce((total, item) => total + Number(item.quantity || 0), 0),
    [cartItems]
  );

  const handleQuantityChange = (variantId, value) => {
    const sanitizedValue = sanitizeQuantityInput(value);
    setDraftQuantities((currentData) => ({
      ...currentData,
      [variantId]: sanitizedValue
    }));
  };

  const handleQuantityBlur = (variantId) => {
    const normalizedValue = draftQuantities[variantId] || "1";
    const nextItems = updateCartItemQuantity(variantId, normalizedValue);
    setCartItemsState(nextItems);
    setDraftQuantities((currentData) => ({
      ...currentData,
      [variantId]: String(Math.max(Number(normalizedValue || 1), 1))
    }));
  };

  const handleRemoveItem = (variantId) => {
    const nextItems = removeCartItem(variantId);
    setCartItemsState(nextItems);
    setDraftQuantities((currentData) => {
      const nextDrafts = { ...currentData };
      delete nextDrafts[variantId];
      return nextDrafts;
    });
  };

  const handleCheckout = async () => {
    if (!cartItems.length) {
      showToast({ message: "Your cart is empty.", variant: "warning" });
      return;
    }

    setSubmitting(true);

    try {
      const response = await placeVendorOrder(
        cartItems.map((item) => ({
          variant_id: Number(item.variant_id),
          quantity: Number(item.quantity)
        }))
      );

      clearCart();
      setCartItemsState([]);
      setDraftQuantities({});
      showToast({
        message: response.message ? `${response.message} Order #${response.orderId}` : "Order placed successfully.",
        variant: "success"
      });
    } catch (error) {
      showToast({ message: error.message || "Checkout failed", variant: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 px-2">
        <div>
          <h3 className="mb-1">Cart</h3>
          <p className="text-muted mb-0">Review selected variants and checkout to place your order.</p>
        </div>
        <div className="small text-muted">
          {totalItems} item{totalItems === 1 ? "" : "s"} selected
        </div>
      </div>
      <Row>
        <Col xs={12} xl={8} className="mb-4 mb-xl-0">
          {cartItems.length === 0 ? (
            <Card border="light" className="shadow-sm">
              <Card.Body className="py-5 text-center">
                <div className="mb-2 fw-bold">Your cart is empty</div>
                <div className="text-muted small">Add some variants from the products page to see them here.</div>
              </Card.Body>
            </Card>
          ) : (
            cartItems.map((item) => (
              <Card border="light" className="shadow-sm mb-3 vendor-cart-item" key={item.variant_id}>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col xs={12} md={7} className="mb-3 mb-md-0">
                      <div>
                        <div className="d-flex gap-3 align-items-center mb-3">
                          {item.product_image_url ? (
                            <img
                              src={resolveAssetUrl(item.product_image_url)}
                              alt={item.product_name}
                              style={{ width: "82px", height: "82px", objectFit: "cover", borderRadius: "1rem" }}
                            />
                          ) : (
                            <div className="border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: "82px", height: "82px", fontSize: "0.75rem" }}>
                              N/A
                            </div>
                          )}
                          <div>
                            <div className="text-uppercase text-muted" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>Product</div>
                            <div className="fw-semibold mt-1">{item.product_name}</div>
                          </div>
                        </div>

                        <div
                          className="d-flex gap-3 align-items-center ms-md-4 ps-md-4 pt-3"
                          style={{ borderTop: "1px solid #edf2f7" }}
                        >
                          {item.variant_image_url ? (
                            <img
                              src={resolveAssetUrl(item.variant_image_url)}
                              alt={item.variant_name}
                              style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "1rem" }}
                            />
                          ) : (
                            <div className="border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: "72px", height: "72px", fontSize: "0.75rem" }}>
                              N/A
                            </div>
                          )}
                          <div>
                            <div className="text-uppercase text-muted" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>Variant</div>
                            <div className="fw-semibold mt-1 mb-1">{item.variant_name}</div>
                            <div className="small text-muted">SKU: {item.sku}</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={5}>
                      <Row>
                        <Col xs={7} className="mb-3">
                          <Form.Group>
                            <Form.Label className="small text-muted">Quantity</Form.Label>
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              value={draftQuantities[item.variant_id] ?? String(item.quantity)}
                              onChange={(event) => {
                                const nextValue = event.target.value;
                                handleQuantityChange(item.variant_id, nextValue);
                              }}
                              onBlur={() => handleQuantityBlur(item.variant_id)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={5} className="mb-3 d-flex align-items-end">
                          <Button variant="outline-danger" className="w-100" onClick={() => handleRemoveItem(item.variant_id)}>
                            <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))
          )}
        </Col>

        <Col xs={12} xl={4}>
          <Card border="light" className="shadow-sm position-sticky" style={{ top: "1.5rem" }}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon icon={faReceipt} className="me-2 text-primary" />
                <h5 className="mb-0">Order Summary</h5>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Items</span>
                <span className="fw-semibold">{totalItems}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between align-items-center mb-4">
                <span className="fw-bold">Ready to place</span>
                <span className="fw-bold fs-5">{totalItems} item{totalItems === 1 ? "" : "s"}</span>
              </div>
              <Button variant="primary" className="w-100" onClick={handleCheckout} disabled={!cartItems.length || submitting}>
                {submitting ? "Processing..." : "Checkout"}
                {!submitting ? <FontAwesomeIcon icon={faArrowRight} className="ms-2" /> : null}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
